import mapboxgl from "mapbox-gl";

import {
  ShapefileStyleProp,
  getDashArray,
  normalizeFillPattern,
  shapefileLayerType
} from "components/project/shapefiles/shapefiles.constants";
import type { ShapefileStylePropT } from "components/project/shapefiles/shapefiles.constants";

import { getColor } from "../hooks/manage-mapbox-shapefile-loading/utils/getColor";
import { ensureFillPatternImage } from "./fillPatterns";
import { setPaintProperty } from "./layerOperations";
import { buildShapefilePointRadius } from "./shapefilePointRadius";

interface UpdateShapefileLayerStylesParams {
  mapbox: mapboxgl.Map;
  shapefileId: string;
  property: ShapefileStylePropT;
  value: string | number;
}

export function updateShapefileLayerStyles({
  mapbox,
  shapefileId,
  property,
  value
}: UpdateShapefileLayerStylesParams): void {
  if (!mapbox || !shapefileId) return;

  const layerId = `${shapefileId}${shapefileLayerType.fill}`;
  const strokeLayerId = `${shapefileId}${shapefileLayerType.stroke}`;
  const pointLayerId = `${shapefileId}${shapefileLayerType.point}`;

  const layer = mapbox.getLayer(layerId);
  const hasPointLayer = mapbox.getLayer(pointLayerId)?.type === "circle";
  if (!layer && !hasPointLayer) {
    return;
  }

  const numericValue = typeof value === "string" ? parseFloat(value) : value;
  const isNumericProperty =
    property === ShapefileStyleProp.Thickness || property === ShapefileStyleProp.Opacity;
  if (isNumericProperty && !Number.isFinite(numericValue)) {
    return;
  }
  const colorValue =
    property === ShapefileStyleProp.Color || property === ShapefileStyleProp.StrokeColor
      ? getColor(String(value))
      : value;

  const updateExpression = (currentValue: unknown, newValue: string | number) => {
    if (Array.isArray(currentValue) && currentValue[0] === "match") {
      const expression = [...currentValue];
      expression[expression.length - 1] = newValue;
      return expression;
    }
    return newValue;
  };

  switch (property) {
    case ShapefileStyleProp.Color:
      if (layer?.type === "fill") {
        const currentColor = mapbox.getPaintProperty(layerId, "fill-color");
        setPaintProperty(
          mapbox,
          layerId,
          "fill-color",
          updateExpression(currentColor, colorValue)
        );
        // Re-generate fill-pattern with new color if one is active
        const currentPattern = mapbox.getPaintProperty(layerId, "fill-pattern");
        if (currentPattern && typeof currentPattern === "string") {
          const patternStr = currentPattern;
          // Pattern image name format: "fill-pattern-{pattern}-{color}"
          const patternParts = patternStr.split("-");
          // Extract pattern name (everything after "fill-pattern-" and before the last segment which is the color)
          if (patternParts.length >= 3) {
            const patternName = patternParts
              .slice(2, -1)
              .join(
                "-"
              ) as import("components/project/shapefiles/shapefiles.constants").FillPattern;
            const newImageName = ensureFillPatternImage(mapbox, patternName, colorValue);
            if (newImageName) {
              setPaintProperty(mapbox, layerId, "fill-pattern", newImageName);
            }
          }
        }
      } else if (layer?.type === "line") {
        const currentColor = mapbox.getPaintProperty(layerId, "line-color");
        setPaintProperty(
          mapbox,
          layerId,
          "line-color",
          updateExpression(currentColor, colorValue)
        );
      }
      if (hasPointLayer) {
        setPaintProperty(mapbox, pointLayerId, "circle-color", colorValue);
      }
      break;

    case ShapefileStyleProp.StrokeColor:
      if (mapbox.getLayer(strokeLayerId)) {
        const currentColor = mapbox.getPaintProperty(strokeLayerId, "line-color");
        setPaintProperty(
          mapbox,
          strokeLayerId,
          "line-color",
          updateExpression(currentColor, colorValue)
        );
      }
      if (hasPointLayer) {
        setPaintProperty(mapbox, pointLayerId, "circle-stroke-color", colorValue);
      }
      break;

    case ShapefileStyleProp.Thickness:
      if (layer?.type === "line") {
        const currentWidth = mapbox.getPaintProperty(layerId, "line-width");
        setPaintProperty(
          mapbox,
          layerId,
          "line-width",
          updateExpression(currentWidth, numericValue)
        );
      }
      if (mapbox.getLayer(strokeLayerId)) {
        const currentWidth = mapbox.getPaintProperty(strokeLayerId, "line-width");
        setPaintProperty(
          mapbox,
          strokeLayerId,
          "line-width",
          updateExpression(currentWidth, numericValue)
        );
      }
      // Uniform radius for instant feedback; the save's success cascade restores per-feature sizes.
      if (hasPointLayer) {
        setPaintProperty(
          mapbox,
          pointLayerId,
          "circle-radius",
          buildShapefilePointRadius(numericValue)
        );
      }
      break;

    case ShapefileStyleProp.Opacity: {
      // Set paint property directly to the numeric value for immediate visual feedback.
      // The mutation cascade will later call updateShapetileFilters which re-applies
      // the coalesce+feature-state expression — but by then the RQ cache is already
      // updated with the correct opacity, so there's no visual flicker.
      if (layer?.type === "fill") {
        setPaintProperty(mapbox, layerId, "fill-opacity", numericValue);
      } else if (layer?.type === "line") {
        setPaintProperty(mapbox, layerId, "line-opacity", numericValue);
      }
      if (mapbox.getLayer(strokeLayerId)) {
        setPaintProperty(mapbox, strokeLayerId, "line-opacity", numericValue);
      }
      if (hasPointLayer) {
        setPaintProperty(mapbox, pointLayerId, "circle-opacity", numericValue);
        setPaintProperty(mapbox, pointLayerId, "circle-stroke-opacity", numericValue);
      }
      break;
    }

    // Point-only shapetiles have no fill or stroke layer, so LineStyle and FillPattern bail early.
    case ShapefileStyleProp.LineStyle: {
      if (!layer) break;
      const dashArray = getDashArray(String(value));
      if (layer.type === "line") {
        setPaintProperty(mapbox, layerId, "line-dasharray", dashArray);
      }
      if (mapbox.getLayer(strokeLayerId)) {
        setPaintProperty(mapbox, strokeLayerId, "line-dasharray", dashArray);
      }
      break;
    }

    // Same as LineStyle: nothing to touch on a point-only shapetile.
    case ShapefileStyleProp.FillPattern: {
      if (!layer) break;
      const pattern = normalizeFillPattern(String(value));
      // We need the current fill color to generate the pattern image
      const currentColor =
        layer.type === "fill"
          ? (mapbox.getPaintProperty(layerId, "fill-color") as string) ?? "#000000"
          : "#000000";
      if (pattern !== "none") {
        const patternImageName = ensureFillPatternImage(mapbox, pattern, currentColor);
        if (patternImageName) {
          if (layer.type === "fill") {
            setPaintProperty(mapbox, layerId, "fill-pattern", patternImageName);
          }
        }
      } else {
        if (layer.type === "fill") {
          setPaintProperty(mapbox, layerId, "fill-pattern", "");
        }
      }
      break;
    }
  }
}
